import React from 'react';

import {
  Typography,
  Button,
  Icon,
  Popup,
  Input,
  Select,
  Notifications,
} from '../../components/index';
import { useFormik } from 'formik';
import { useNavigate } from 'react-router-dom';
import { useResponsivity, scrollToTop } from '../../helpers/helpers';
import * as _ from 'lodash';
import Styled from './PageOverview.style';
import * as Yup from 'yup';

interface MeterReadProps {
  listLocation: any;
  location: any;
  meterRead: any;
  submitMeterRead: Function;
}

export const MeterReadBanner: React.FC<MeterReadProps> = (props) => {
  const { listLocation, submitMeterRead, meterRead } = props;

  const data = _.get(listLocation, 'data.data');
  const { error } = _.get(meterRead, 'submitMeterRead.status');
  const meterReadData = _.get(meterRead, 'submitMeterRead.data.message');

  const isMobile = useResponsivity();
  const navigate = useNavigate();

  const [popup, setPopup] = React.useState(false);

  const renderMapOptions =
    data?.map((el) => ({
      value: el.id,
      label: el.property_name,
    })) || [];

  const MeterReadSchema = Yup.object().shape({
    meterRead: Yup.string()
      .matches(/^\d+$/, 'The field should have digits only')
      .required('Required'),
    propertyId: Yup.string().required('Required'),
  });

  const formik = useFormik({
    initialValues: {
      meterRead: '',
      propertyId: '',
    },
    validationSchema: MeterReadSchema,
    onSubmit: async (values, { setSubmitting, resetForm }) => {
      try {
        await submitMeterRead(values.propertyId, values.meterRead);

        // Only reset after successful submission
        resetForm();
        scrollToTop();
      } catch (error) {
        console.error('Failed to submit meter reading:', error);
        setPopup(false);
      } finally {
        setSubmitting(false);
        setPopup(false);
      }
    },
  });
  const findLocation = _.find(data, function (o) {
    return o.id == parseInt(formik.values.propertyId);
  });

  const propertyName = findLocation && findLocation.property_name;

  const {
    values,
    errors,
    touched,
    handleChange,
    handleBlur,
    handleSubmit,
    isSubmitting,
  } = formik;

  const handleOpenPopup = () => {
    // Run validation before opening the confirmation popup
    formik.validateForm().then((validationErrors) => {
      if (Object.keys(validationErrors).length === 0) {
        setPopup(true);
      } else {
        formik.setTouched({
          meterRead: true,
          propertyId: true,
        });
      }
    });
  };

  return (
    <Styled.MeterBanner>
      <Icon icon="meter" size={100} />

      <Typography
        size="1"
        style={{
          fontSize: '30px',
          width: '600px',
          textAlign: 'center',
          margin: '16px 0px 32px 0px',
        }}
        text="Tell us your meter reading & track your water-saving progress"
      />

      <form onSubmit={handleSubmit} style={{ width: '100%' }}>
        <div style={{ display: 'flex', justifyContent: 'center' }}>
          <Select
            value={values.propertyId}
            onChange={handleChange}
            onBlur={handleBlur}
            options={renderMapOptions}
            name="propertyId"
            placeholder="Select location"
            style={{
              height: '50px',
              borderRadius: '8px 0px 0px 8px',
              width: '425px',
              marginBottom: '16px',
            }}
            error={
              errors.propertyId && touched.propertyId
                ? errors.propertyId
                : undefined
            }
          />
        </div>

        <div style={{ display: 'flex', justifyContent: 'center' }}>
          <Input
            id="meterRead"
            name="meterRead"
            value={values.meterRead}
            type="text"
            placeholder="Water meter reading"
            onChange={handleChange}
            onBlur={handleBlur}
            style={{
              height: '50px',
              borderRadius: '8px 0px 0px 8px',
              width: '300px',
            }}
            error={
              errors.meterRead && touched.meterRead
                ? errors.meterRead
                : undefined
            }
          />

          <Button
            type="button"
            style={{
              height: '50px',
              borderRadius: '0px 8px 8px 0px',
            }}
            appearance="REVERSE"
            disabled={isSubmitting}
            onClick={handleOpenPopup}
          >
            Submit
            <Icon
              icon="arrow"
              size={20}
              colorPrimary="white"
              colorSecondary="white"
              style={{ marginLeft: '16px' }}
            />
          </Button>

          <Popup
            isDesktop={!isMobile}
            open={popup}
            handleClick={() => {
              setPopup(false);
            }}
          >
            <Typography size="3" text="Are you sure?" />
            <Typography size="p" text={`Meter read: ${values.meterRead}`} />
            <Typography size="p" text={`Location: ${propertyName}`} />
            <Button
              type="submit"
              style={{
                height: '50px',
                borderRadius: '8px',
              }}
              appearance="REVERSE"
              disabled={isSubmitting}
            >
              Submit
              <Icon
                icon="arrow"
                size={20}
                colorPrimary="white"
                colorSecondary="white"
                style={{ marginLeft: '16px' }}
              />
            </Button>
          </Popup>
        </div>
        {meterReadData && (
          <div style={{ display: 'flex', justifyContent: 'center' }}>
            <Notifications type="SUCCESS">{meterReadData}</Notifications>
          </div>
        )}
        {error && (
          <div style={{ display: 'flex', justifyContent: 'center' }}>
            <Notifications type="ERROR">{error}</Notifications>
          </div>
        )}
      </form>

      <div
        style={{
          cursor: 'pointer',
          textDecoration: 'underline',
          fontWeight: 500,
          marginBottom: '8px',
        }}
        onClick={() => navigate('/meter-read')}
      >
        View history
      </div>
    </Styled.MeterBanner>
  );
};

export default MeterReadBanner;
